import { useState, useCallback } from 'react';
import { useEditorStore } from '../store/editorStore';
import { exportTimeline, type ExportSegment } from '../engine/ffmpegBridge';
import { getProxy } from '../engine/proxyManager';
import { renderAudioMix, encodeWav } from '../engine/audioExporter';
import { type Clip, type ExportQuality, EXPORT_QUALITY_PRESETS } from '../types';

export type ExportFormat = 'mp4' | 'wav' | 'mp4-video-only';

/**
 * Break the timeline into segments based on video and audio clip boundaries.
 */
function computeSegments(
  videoClips: Clip[],
  audioClips: Clip[],
): { timelineStartMs: number; durationMs: number; hasAudio: boolean; videoClip: Clip }[] {
  const segments: { timelineStartMs: number; durationMs: number; hasAudio: boolean; videoClip: Clip }[] = [];

  for (const vClip of videoClips) {
    const vStart = vClip.startMs;
    const vEnd = vClip.startMs + vClip.durationMs;

    const breakpoints = new Set<number>();
    breakpoints.add(vStart);
    breakpoints.add(vEnd);

    for (const aClip of audioClips) {
      const aStart = aClip.startMs;
      const aEnd = aClip.startMs + aClip.durationMs;
      if (aStart < vEnd && aEnd > vStart) {
        if (aStart > vStart) breakpoints.add(aStart);
        if (aEnd < vEnd) breakpoints.add(aEnd);
      }
    }

    const sorted = Array.from(breakpoints).sort((a, b) => a - b);

    for (let i = 0; i < sorted.length - 1; i++) {
      const segStart = sorted[i];
      const segEnd = sorted[i + 1];
      if (segEnd <= segStart) continue;

      const midpoint = (segStart + segEnd) / 2;
      const hasAudio = audioClips.some(
        (ac) => midpoint >= ac.startMs && midpoint < ac.startMs + ac.durationMs,
      );

      segments.push({ timelineStartMs: segStart, durationMs: segEnd - segStart, hasAudio, videoClip: vClip });
    }
  }

  segments.sort((a, b) => a.timelineStartMs - b.timelineStartMs);
  return segments;
}

export function useExport() {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const [phase, setPhase] = useState<string>('');

  const startExport = useCallback(async (format: ExportFormat = 'mp4', quality: ExportQuality = '1080p') => {
    const { project } = useEditorStore.getState();
    const qualityPreset = EXPORT_QUALITY_PRESETS[quality];

    // WAV export — render audio mix only
    if (format === 'wav') {
      setExporting(true);
      setProgress(0);
      setPhase('');
      setError(null);
      try {
        setPhase('downloading');
        setProgress(5);
        // Download originals for higher quality audio
        const originalDataMap = new Map<string, ArrayBuffer>();
        const uniqueAssetIds = [...new Set(project.assets.map((a) => a.id))];
        for (let i = 0; i < uniqueAssetIds.length; i++) {
          const asset = project.assets.find((a) => a.id === uniqueAssetIds[i]);
          if (!asset?.originalUrl) continue;
          try {
            const resp = await fetch(asset.originalUrl);
            if (resp.ok) originalDataMap.set(asset.id, await resp.arrayBuffer());
          } catch { /* fall back to proxy in renderAudioMix */ }
          setProgress(5 + Math.round(((i + 1) / uniqueAssetIds.length) * 5));
        }
        setPhase('mixing');
        setProgress(10);
        const audioBuffer = await renderAudioMix(originalDataMap);
        setProgress(80);
        const wavData = encodeWav(audioBuffer, 16);
        setProgress(95);

        const blob = new Blob([wavData], { type: 'audio/wav' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${project.name || 'export'}.wav`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setProgress(100);
      } catch (err) {
        setError(String(err));
      } finally {
        setExporting(false);
      }
      return;
    }

    // MP4 export
    const videoClips: (Clip & { _assetId: string })[] = [];
    const audioClips: Clip[] = [];

    for (const track of project.tracks) {
      for (const clip of track.clips) {
        if (track.type === 'video') {
          const asset = project.assets.find((a) => a.id === clip.assetId);
          if (asset?.originalUrl || asset?.proxyKey) {
            videoClips.push({ ...clip, _assetId: clip.assetId });
          }
        } else if (track.type === 'audio') {
          audioClips.push(clip);
        }
      }
    }

    if (videoClips.length === 0) {
      setError('No clips to export');
      return;
    }

    // For mp4-video-only, strip audio
    const segments = computeSegments(videoClips, format === 'mp4-video-only' ? [] : audioClips);

    if (segments.length === 0) {
      setError('No segments to export');
      return;
    }

    setExporting(true);
    setProgress(0);
    setPhase('downloading');
    setError(null);

    try {
      // Download originals from R2 (or fall back to proxy)
      const originalCache = new Map<string, ArrayBuffer>();
      const uniqueAssetIds = [...new Set(videoClips.map((c) => c._assetId))];

      for (let i = 0; i < uniqueAssetIds.length; i++) {
        const assetId = uniqueAssetIds[i];
        const asset = project.assets.find((a) => a.id === assetId);
        let fileData: ArrayBuffer | null = null;

        // Try original URL first
        if (asset?.originalUrl) {
          try {
            const resp = await fetch(asset.originalUrl);
            if (resp.ok) fileData = await resp.arrayBuffer();
          } catch { /* fall back to proxy */ }
        }

        // Fall back to proxy
        if (!fileData) {
          fileData = await getProxy(assetId);
        }

        if (!fileData) throw new Error(`Missing source for asset ${assetId}`);
        originalCache.set(assetId, fileData);
        setProgress(Math.round(((i + 1) / uniqueAssetIds.length) * 15));
      }

      const exportSegments: ExportSegment[] = segments.map((seg) => {
        const fileData = originalCache.get(seg.videoClip.assetId)!;
        const offsetInVideoClip = seg.timelineStartMs - seg.videoClip.startMs;
        const sourceOffsetMs = seg.videoClip.sourceOffsetMs + offsetInVideoClip;
        return { fileData: fileData.slice(0), sourceOffsetMs, durationMs: seg.durationMs, hasAudio: seg.hasAudio };
      });

      // Render audio mix offline (includes all effects, fades, volume, pan)
      let audioMixData: ArrayBuffer | undefined;
      if (format !== 'mp4-video-only') {
        setPhase('mixing');
        setProgress(15);
        const audioBuffer = await renderAudioMix(originalCache);
        audioMixData = encodeWav(audioBuffer, 16);
        setProgress(20);
      }

      setPhase('encoding');
      const exportId = crypto.randomUUID();
      const result = await exportTimeline(exportId, exportSegments, (p) => setProgress(20 + Math.round(p * 0.80)), audioMixData, qualityPreset);

      const blob = new Blob([result.outputData], { type: 'video/mp4' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${project.name || 'export'}.mp4`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setProgress(100);
    } catch (err) {
      setError(String(err));
    } finally {
      setExporting(false);
    }
  }, []);

  return { exporting, progress, phase, error, startExport };
}
